import { useCallback, useEffect, useMemo, useState } from "react";
import Styles from "./styles.module.scss";
import SearchBar from "@Atom/SearchBar";
import JobSeekersCard from "@Molecule/JobSeekersCard";
import Images from "@Theme/Images";
import {
  createCandidateBookmark,
  deleteCandidateBookmark,
  getDataCandidateBookmark,
} from "@Services/home";
import { capitalizedFirstLetter } from "@Helpers/capitalizedFirstLetter";
import { Button, Pagination } from "@mui/material";
import ImgSingleBag from "@Assets/Images/single-bag.png";
import { useNavigate } from "react-router-dom";
import Toast from "@Atom/Toast";
import EmptyData from "@Organism/Recruiter/PipelineDetail/EmptyData";
import ModalWrapper from "@Atom/ModalWrapper";
import JobPostingLinkModal from "@Molecule/_modal/JobPostingLinkModal";
import moment from "moment";
import { useDebounce } from "@Hooks/useDebounce";
import { fileBaseUrl } from "@Config/api";

export default function ArchivedApplication() {
  const [search, setSearch] = useState("");
  const [data, setData] = useState([]);
  const [isLoadingGet, setIsLoadingGet] = useState(true);

  const navigate = useNavigate();
  const [limit] = useState(10);
  const [pages, setPages] = useState(1);
  const [dataList, setDataList] = useState({ totalData: 1, totalPage: 1 });
  const debounceSearchQuery = useDebounce(search, 500);

  const refetchData = useCallback(
    async (isLoadingRefetch = true) => {
      setIsLoadingGet(isLoadingRefetch);
      try {
        const { response: res } = await getDataCandidateBookmark(
          limit,
          pages,
          debounceSearchQuery
        );
        setData(res?.data);
        setDataList(res);
      } catch (error) {
        console.log("Error", error);
      } finally {
        setIsLoadingGet(false);
      }
    },
    [debounceSearchQuery, pages, limit]
  );

  useEffect(() => {
    refetchData(true);
  }, [refetchData]);

  useEffect(() => {
    setPages(1);
  }, [search]);

  const dataTemplate = useMemo(() => {
    return data?.map((el) => ({
      id: el?.jobPostID,
      name: capitalizedFirstLetter(el?.title),
      experience: el?.experience,
      workType: el?.workType,
      contractType: el?.contractType,
      workMethod: el?.workMethod,
      client: el?.clientName,
      location: el?.placement,
      salary: `${el?.salary[0]} - ${el?.salary[1]}`,
      createdAt: moment(el?.jobPostCreatedAt).startOf("day").fromNow(),
      // status: "Publish",
      img:
        el?.workType === "External"
          ? fileBaseUrl + el?.iconUri
          : Images.PERAMATA_LOGO_CICRLE,
      isApply: el?.hasApplied,
      isArchived: el?.isBookmarked || true,
      jobPostID: el?.jobPostID,
      _id: el?.jobPostID, // require for the <JobPostingLinkModal /> component
    }));
  }, [data]);

  const createArchiveJob = async (jobPostID) => {
    try {
      await createCandidateBookmark(jobPostID);
      refetchData(false);
    } catch (error) {
      console.log("Error create bookmark candidate", error);
    }
  };

  const deleteBookmark = async (jobPostID) => {
    try {
      await deleteCandidateBookmark(jobPostID);
      refetchData();
    } catch (error) {
      console.log("Error delete bookmark candidate", error);
    }
  };

  const [error, setError] = useState(null);
  const [showModalLink, setShowModalLink] = useState("");

  const handleForm = (el) => {
    navigate(`/job-seeker/${el?._id}`);
  };

  useEffect(() => {
    setTimeout(() => {
      if (error) setError(false);
    }, 2000);
  }, [error]);

  const backToTop = (top = 0) => {
    return window.scrollTo({
      behavior: "smooth",
      top: top,
    });
  };

  useEffect(() => {
    backToTop();
  }, [pages]);

  return (
    <section className={Styles.flexCol}>
      <div className={Styles.right}>
        <div className={Styles.header}>
          <div className={Styles.left}>
            <span className={Styles.title}>Loker Disimpan</span>
            <span className={Styles.total}>{dataList?.totalData}</span>
          </div>

          <div className={Styles.rightHeader}>
            <SearchBar
              value={search}
              placeholder={"Cari nama pekerjaan"}
              onChange={(e) => setSearch(e?.target?.value)}
            />
          </div>
        </div>

        <Toast
          isSent={error}
          label={"Job Posting Not Found"}
          bg="red"
          icon="cross-circle"
        />

        <div className={Styles.content}>
          {dataTemplate?.length === 0 && !isLoadingGet && !search ? (
            <EmptyHandler />
          ) : dataTemplate?.length === 0 && !isLoadingGet && search ? (
            <div className={Styles.wrapEmptyHandler}>
              <div className={Styles.wrapEmptyHandler}>
                <EmptyData
                  customLabel={"Loker tersimpan yang anda cari tidak ada"}
                />
              </div>
            </div>
          ) : (
            <div className={Styles.listBox}>
              {(isLoadingGet ? [1, 2] : dataTemplate)?.map((el, idx) => {
                return (
                  <JobSeekersCard
                    key={idx}
                    data={el}
                    loading={isLoadingGet}
                    setShowDetail={() => handleForm(el)}
                    onClickArchiveIcon={() =>
                      el?.isArchived
                        ? deleteBookmark(el?.id)
                        : createArchiveJob(el?.id)
                    }
                  />
                );
              })}
              {dataTemplate.length === 1 &&
                Array.from({ length: 1 }).map((_, idx) => (
                  <div key={idx} className={Styles.cardFilledEmpty}></div>
                ))}
            </div>
          )}

          {showModalLink && (
            <ModalWrapper
              show={showModalLink}
              handleClose={() => setShowModalLink(null)}
              isLandingPage
            >
              <JobPostingLinkModal
                data={showModalLink}
                handleClose={() => setShowModalLink(null)}
              />
            </ModalWrapper>
          )}
        </div>
      </div>
      <div>
        <Pagination
          count={dataList?.totalPage}
          page={pages}
          onChange={(_, value) => setPages(value)}
          color="primary"
        />
      </div>
    </section>
  );
}

const EmptyHandler = () => {
  const navigate = useNavigate();
  return (
    <div className={Styles.wrapEmpty}>
      <img src={ImgSingleBag} alt="empty-handler-img" />
      <h2>Tidak Ada Loker Yang Disimpan</h2>
      <Button
        onClick={() => navigate("/job-seeker")}
        className={Styles.btn}
        variant="outlined"
      >
        Mulai Cari Loker
      </Button>
    </div>
  );
};
